import React, { useState } from 'react';
import { Card } from 'react-bootstrap';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import InsightPage, {
  StatRow, StatCard, CopyStats, ControlSelect, useChartTheme,
  PHONE_CHART_HEIGHT, PHONE_CHART_MARGIN, PHONE_X_AXIS, PHONE_Y_AXIS,
} from '../../components/insights/InsightPage';
import { insightsService } from '../../services/apiService';
import { useInsightData } from '../../hooks/useInsightData';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { formatNumber } from '../../utils/format';

/** "So much for hot desking" holds only when people mostly sit at the same desk. */
const LOYAL_AVERAGE = 50;

const DeskLoyalty = () => {
  const isMobile = useIsMobile();
  const t = useChartTheme();
  const [minBookings, setMinBookings] = useState(10);
  const { data, loading, error, reload } = useInsightData(() => insightsService.deskLoyalty(minBookings), [minBookings]);
  const d = data || {};
  const histogram = d.histogram || [];

  const copy = d.regularBookers > 0
    ? [
      d.avgPctSameDesk >= LOYAL_AVERAGE && 'So much for hot desking.',
      `${d.pctCreaturesOfHabit}% of regular desk-bookers sit at the same desk 80%+ of the time.`,
      `On average, people book their favourite desk ${d.avgPctSameDesk}% of the time. 🪑`,
    ].filter(Boolean).join(' ')
    : '';

  const controls = (
    <ControlSelect id="deskLoyaltyMinimum" label="Regular bookers have at least" value={minBookings} onChange={(e) => setMinBookings(Number(e.target.value))}>
      {[5, 10, 20, 30].map((v) => <option key={v} value={v}>{v} bookings</option>)}
    </ControlSelect>
  );

  return (
    <InsightPage
      title="The golden desk"
      description="How loyal are people to their favourite desk?"
      controls={controls}
      loading={loading}
      error={error}
      onRetry={reload}
      ready={data != null}
      empty={!d.regularBookers}
      emptyMessage={`Nobody has made ${minBookings} or more desk bookings yet`}
    >
      <StatRow>
        <StatCard label="Regular bookers" value={formatNumber(d.regularBookers)} sub={`${minBookings}+ bookings`} />
        <StatCard label="Average same-desk rate" value={`${d.avgPctSameDesk}%`} variant="primary" />
        <StatCard label="Creatures of habit" value={formatNumber(d.creaturesOfHabit)} sub="Same desk 80%+ of the time" />
        <StatCard label="Share who are creatures of habit" value={`${d.pctCreaturesOfHabit}%`} />
      </StatRow>

      <Card className="shadow-sm">
        <Card.Header className="bg-light"><h5 className="mb-0">Distribution: share of bookings at favourite desk</h5></Card.Header>
        <Card.Body>
          <ResponsiveContainer width="100%" height={isMobile ? PHONE_CHART_HEIGHT : 400}>
            <BarChart data={histogram} margin={isMobile ? PHONE_CHART_MARGIN : { top: 10, right: 30, left: 10, bottom: 20 }}>
              <CartesianGrid {...t.grid} />
              <XAxis dataKey="bucket" {...t.xAxis} {...(isMobile ? PHONE_X_AXIS : {})} />
              <YAxis allowDecimals={false} {...t.yAxis} {...(isMobile ? PHONE_Y_AXIS : {})} />
              <Tooltip {...t.tooltip} formatter={(v) => formatNumber(v)} />
              <Bar dataKey="bookers" name="Bookers" fill={t.colors.emphasis} />
            </BarChart>
          </ResponsiveContainer>
        </Card.Body>
      </Card>

      {copy && <CopyStats text={copy} />}
    </InsightPage>
  );
};

export default DeskLoyalty;
